import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import { Loader2, Monitor, SlidersHorizontal, MapPin, User, Phone, Key, Hash, Calendar, FileText } from "lucide-react";
import TomSelect from "tom-select";
import { motion, AnimatePresence } from "framer-motion";

const API_BASE = "https://jne-cilacap.com/slip/public/api";
const ALLOWED_LOCATION_PREFIXES = [
  "depan",
  "ruang",
  "kp",
  "agen",
  "counter",
  "cash",
  "outbound",
  "inbound",
  "delivery",
  "undel",
  "pickup",
];

const filterAllowedLocations = (items = []) => {
  const filtered = items.filter((item) => {
    const name = String(item?.nama || item?.name || "").toLowerCase().trim();
    return ALLOWED_LOCATION_PREFIXES.some((prefix) => name.startsWith(prefix));
  });

  return filtered.length ? filtered : items;
};

const PERANGKAT_OPTIONS = [
  { id: "Komputer", nama: "💻 Komputer" },
  { id: "Laptop", nama: "💻 Laptop" },
  { id: "Printer", nama: "🖨️ Printer" },
  { id: "Hybrid", nama: "📟 Hybrid" },
  { id: "Scanner", nama: "📷 Scanner" },
  { id: "Network", nama: "🌐 Network / Jaringan" },
  { id: "Software", nama: "💾 Software / Aplikasi" },
  { id: "Lainnya", nama: "📝 Lainnya" },
];

const PENANGANAN_OPTIONS = [
  { id: "Anydesk", nama: "🔴 Anydesk" },
  { id: "Rustdesk", nama: "🔵 Rustdesk" },
  { id: "Ultraviewer", nama: "🔷 Ultraviewer" },
  { id: "On-site", nama: "🏢 On-site / Datang Langsung" },
  { id: "Telepon", nama: "📞 Telepon / WA Call" },
  { id: "Lainnya", nama: "📝 Lainnya" },
];

// ── TomSelect wrapper ─────────────────────────────────────────────────────────
const TomSelectField = ({ selectRef, options, value, onChange, placeholder, allowCreate = false }) => {
  const instanceRef = useRef(null);

  useEffect(() => {
    if (!selectRef.current) return;

    if (instanceRef.current) {
      const cur = instanceRef.current.getValue();
      instanceRef.current.clearOptions();
      instanceRef.current.addOptions(options);
      instanceRef.current.refreshOptions(false);
      if (value) {
        instanceRef.current.setValue(value, true);
      } else if (cur) {
        instanceRef.current.setValue(cur, true);
      }
      return;
    }

    const ts = new TomSelect(selectRef.current, {
      valueField: "id",
      labelField: "nama",
      searchField: ["nama", "id"],
      placeholder: placeholder || "-- Pilih --",
      options: options || [],
      create: allowCreate,
      maxItems: 1,
      render: {
        option: (d, esc) => `
          <div style="padding:8px 12px;border-bottom:1px solid #f1f5f9">
            <div style="font-weight:600;color:#1e293b;font-size:13px">${esc(d.nama || d.id || "")}</div>
            ${d.sub ? `<div style="font-size:11px;color:#64748b">${esc(d.sub)}</div>` : ""}
          </div>`,
        item: (d, esc) => `<div style="font-weight:500;font-size:13px">${esc(d.nama || d.id || "")}</div>`,
      },
      onChange: (v) => onChange(v),
    });

    if (value) {
      ts.setValue(value, true);
    }

    instanceRef.current = ts;

    return () => {
      ts.destroy();
      instanceRef.current = null;
    };
  }, [options]);

  useEffect(() => {
    if (instanceRef.current && value !== undefined) {
      if (instanceRef.current.getValue() !== value) {
        instanceRef.current.setValue(value || "", true);
      }
    }
  }, [value]);

  return null;
};

const RequestITForm = ({ onSuccess, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [loadingLoc, setLoadingLoc] = useState(false);

  // Select refs
  const lokasiRef = useRef(null);
  const perangkatRef = useRef(null);
  const penangananRef = useRef(null);

  const getKaryawanData = () => {
    try {
      return JSON.parse(localStorage.getItem("data_karyawan")) || {};
    } catch {
      return {};
    }
  };

  const getFullName = (karyawan = {}) => {
    const depan = (karyawan.nama_depan || "").trim();
    const belakang = (karyawan.nama_belakang || "").trim();
    const combined = [depan, belakang].filter(Boolean).join(" ");
    return combined || karyawan.nama || karyawan.name || "";
  };

  const initialKaryawan = getKaryawanData();
  const initialFullName = getFullName(initialKaryawan);
  const initialPhone =
    initialKaryawan.no_hp ||
    initialKaryawan.no_wa ||
    initialKaryawan.telepon ||
    initialKaryawan.hp ||
    initialKaryawan.phone ||
    "";

  const [form, setForm] = useState({
    tanggal: new Date().toISOString().split("T")[0],
    dari: "",
    perangkat: "",
    problem: "",
    penanganan: "Ultraviewer",
    nomer: "",
    password: "",
    pelapor: initialFullName,
    wa_pelapor: initialPhone,
  });

  const [errors, setErrors] = useState({});

  // Load locations from API & auto-select lokasi sesuai nama agen jika tersedia
  useEffect(() => {
    const fetchLocations = async () => {
      setLoadingLoc(true);
      try {
        const res = await fetch(`${API_BASE}/asset-locations`);
        const json = await res.json();
        if (json.status === "success" && Array.isArray(json.data)) {
          const mapped = json.data.map((loc) => ({
            id: loc.name,
            nama: loc.name,
          }));
          const allowed = filterAllowedLocations(mapped);
          setLocations(allowed);

          // Auto-default lokasi berdasarkan nama agen (jika user adalah agen)
          try {
            const k = JSON.parse(localStorage.getItem("data_karyawan") || "{}");
            const u = JSON.parse(localStorage.getItem("data_user") || "{}");
            const isAgen =
              !!k.is_agen || !!u.is_agen ||
              String(k.role || "").toLowerCase() === "agen" ||
              String(u.role || "").toLowerCase() === "agen" ||
              localStorage.getItem("is_agen") === "true" ||
              (Boolean(k.nik) && !String(k.nik).toUpperCase().startsWith("CXP"));

            if (isAgen) {
              const agenNama = k.agen || k.nama_agen || u.agen || u.nama_agen || k.kantor || "";
              const searchKeyLow = agenNama.toLowerCase().trim();

              // Ekstrak kata kunci utama (singkirkan kata umum)
              const agenKeywords = searchKeyLow
                .split(/\s+/)
                .filter(w => w.length > 2 && !['agen', 'counter', 'jne', 'kantor', 'cabang'].includes(w));

              const matched =
                // 1. Exact match
                allowed.find(l => l.nama.toLowerCase().trim() === searchKeyLow) ||
                // 2. Semua keyword cocok
                (agenKeywords.length > 0 && allowed.find(l => {
                  const lNama = l.nama.toLowerCase();
                  return agenKeywords.every(kw => lNama.includes(kw));
                })) ||
                // 3. Keyword terpanjang ada di nama lokasi
                (agenKeywords.length > 0 && allowed.find(l => {
                  const lNama = l.nama.toLowerCase();
                  const longestKw = [...agenKeywords].sort((a, b) => b.length - a.length)[0];
                  return lNama.includes(longestKw);
                })) ||
                // 4. searchKey mengandung nama lokasi
                allowed.find(l => searchKeyLow.includes(l.nama.toLowerCase()));

              if (matched) {
                console.log(`📍 [IT] Default lokasi dari agen: "${matched.nama}"`);
                setForm(prev => ({ ...prev, dari: matched.nama }));
              }
            }
          } catch (e) {
            console.warn("Gagal auto-detect lokasi agen:", e);
          }
        }
      } catch (err) {
        console.warn("Gagal mengambil daftar lokasi:", err);
      } finally {
        setLoadingLoc(false);
      }
    };

    fetchLocations();
  }, []);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleSelectField = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      // When changing penanganan to non-remote, clear nomer & password
      if (field === "penanganan") {
        const isRemote = ["anydesk", "rustdesk", "ultraviewer"].includes(
          (value || "").toLowerCase().trim()
        );
        if (!isRemote) {
          next.nomer = "";
          next.password = "";
        }
      }
      return next;
    });
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  // Check if current penanganan is remote-based (Anydesk / Rustdesk / Ultraviewer)
  const isRemoteTool = ["anydesk", "rustdesk", "ultraviewer"].includes(
    (form.penanganan || "").toLowerCase().trim()
  );

  const isUltraviewer = (form.penanganan || "").toLowerCase().trim() === "ultraviewer";

  const validate = () => {
    const newErrors = {};
    if (!form.tanggal) newErrors.tanggal = "Tanggal wajib diisi";
    if (!form.dari.trim()) newErrors.dari = "Asal / Lokasi wajib dipilih";
    if (!form.perangkat) newErrors.perangkat = "Perangkat wajib dipilih";
    if (!form.problem.trim()) newErrors.problem = "Keterangan masalah wajib diisi";
    if (!form.pelapor.trim()) newErrors.pelapor = "Nama pelapor wajib diisi";

    if (isRemoteTool && !form.nomer.trim()) {
      newErrors.nomer = `ID ${form.penanganan} wajib diisi`;
    }

    if (isUltraviewer && !form.password.trim()) {
      newErrors.password = "Password wajib diisi untuk UltraViewer";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const userId = initialKaryawan?.id;
    if (!userId) {
      await Swal.fire({
        icon: "error",
        title: "Sesi Habis",
        text: "Data karyawan tidak ditemukan, silakan login ulang.",
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        user_created: userId,
      };

      const response = await fetch(`${API_BASE}/request-it`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok && (result.status === "success" || result.id)) {
        await Swal.fire({
          icon: "success",
          title: "Berhasil!",
          text: "Tiket IT Request berhasil dikirim.",
          timer: 2000,
          showConfirmButton: false,
        });
        onSuccess?.();
      } else {
        throw new Error(result.message || "Gagal menyimpan data");
      }
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "Gagal",
        text: err.message || "Terjadi kesalahan, coba lagi.",
      });
    } finally {
      setLoading(false);
    }
  };

  /* ─────────── STYLES ─────────── */
  const s = {
    field: { marginBottom: 18 },
    label: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      marginBottom: 6,
      fontSize: 13,
      fontWeight: 600,
      color: "#374151",
    },
    required: { color: "#ef4444", marginLeft: 2 },
    input: {
      width: "100%",
      padding: "11px 14px",
      borderRadius: 12,
      border: "1.5px solid #e5e7eb",
      fontSize: 14,
      backgroundColor: "#fff",
      outline: "none",
      boxSizing: "border-box",
      transition: "border-color 0.2s",
      color: "#111827",
    },
    inputReadOnly: {
      backgroundColor: "#f8fafc",
      color: "#334155",
      fontWeight: 600,
      cursor: "default",
    },
    inputError: {
      borderColor: "#ef4444",
      backgroundColor: "#fff7f7",
    },
    errorMsg: {
      fontSize: 11.5,
      color: "#ef4444",
      marginTop: 4,
    },
    textarea: {
      resize: "vertical",
      minHeight: 85,
    },
    row: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 14,
    },
    btnRow: {
      display: "flex",
      gap: 10,
      marginTop: 24,
    },
    btnPrimary: {
      flex: 1,
      padding: "13px 0",
      borderRadius: 14,
      border: "none",
      background: "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
      color: "#fff",
      fontWeight: 700,
      fontSize: 15,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      boxShadow: "0 4px 14px rgba(99,102,241,0.35)",
    },
    btnSecondary: {
      padding: "13px 20px",
      borderRadius: 14,
      border: "1.5px solid #e5e7eb",
      background: "#fff",
      color: "#374151",
      fontWeight: 600,
      fontSize: 15,
      cursor: "pointer",
    },
    remoteBox: {
      background: "linear-gradient(135deg, #eff6ff 0%, #f5f3ff 100%)",
      border: "1.5px dashed #818cf8",
      borderRadius: 16,
      padding: "16px",
      marginBottom: 18,
    },
    remoteHeader: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 13,
      fontWeight: 700,
      color: "#4338ca",
      marginBottom: 12,
    },
  };

  const fieldError = (name) =>
    errors[name] ? <p style={s.errorMsg}>⚠ {errors[name]}</p> : null;

  const inputStyle = (name) => ({
    ...s.input,
    ...(errors[name] ? s.inputError : {}),
  });

  return (
    <form onSubmit={handleSubmit} noValidate>
      {/* ── Baris 1: Tanggal + Dari/Lokasi (TomSelect) ── */}
      <div style={s.row}>
        <div style={s.field}>
          <label style={s.label}>
            <Calendar size={15} color="#6366f1" />
            Tanggal<span style={s.required}>*</span>
          </label>
          <input
            type="date"
            name="tanggal"
            value={form.tanggal}
            onChange={handleChange}
            style={{
              ...inputStyle("tanggal"),
              minHeight: 46,
              height: 46,
              lineHeight: 1.2,
              fontSize: 14,
              padding: "10px 12px",
            }}
          />
          {fieldError("tanggal")}
        </div>

        <div style={s.field}>
          <label style={s.label}>
            <MapPin size={15} color="#6366f1" />
            Dari / Lokasi<span style={s.required}>*</span>
          </label>
          {loadingLoc ? (
            <div style={{ fontSize: 13, color: "#94a3b8", padding: "10px 0" }}>
              Memuat daftar lokasi...
            </div>
          ) : (
            <div>
              <select
                ref={lokasiRef}
                autoComplete="off"
                defaultValue={form.dari}
                style={{ width: "100%" }}
              >
                <option value="">Cari dan pilih...</option>
              </select>
              <TomSelectField
                selectRef={lokasiRef}
                options={locations}
                value={form.dari}
                onChange={(val) => handleSelectField("dari", val)}
                placeholder="Cari dan pilih..."
              />
            </div>
          )}
          {fieldError("dari")}
        </div>
      </div>

      {/* ── Baris 2: Perangkat (TomSelect) + Penanganan (TomSelect) ── */}
      <div style={s.row}>
        <div style={s.field}>
          <label style={s.label}>
            <Monitor size={15} color="#6366f1" />
            Perangkat<span style={s.required}>*</span>
          </label>
          <div>
            <select
              ref={perangkatRef}
              autoComplete="off"
              defaultValue={form.perangkat}
              style={{ width: "100%" }}
            >
              <option value="">Pilih Perangkat...</option>
            </select>
            <TomSelectField
              selectRef={perangkatRef}
              options={PERANGKAT_OPTIONS}
              value={form.perangkat}
              onChange={(val) => handleSelectField("perangkat", val)}
              placeholder="Pilih Perangkat..."
            />
          </div>
          {fieldError("perangkat")}
        </div>

        <div style={s.field}>
          <label style={s.label}>
            <SlidersHorizontal size={15} color="#6366f1" />
            Penanganan
          </label>
          <div>
            <select
              ref={penangananRef}
              autoComplete="off"
              defaultValue={form.penanganan}
              style={{ width: "100%" }}
            >
              <option value="">Pilih Penanganan...</option>
            </select>
            <TomSelectField
              selectRef={penangananRef}
              options={PENANGANAN_OPTIONS}
              value={form.penanganan}
              onChange={(val) => handleSelectField("penanganan", val)}
              placeholder="Pilih Penanganan..."
            />
          </div>
        </div>
      </div>

      {/* ── Problem ── */}
      <div style={s.field}>
        <label style={s.label}>
          <FileText size={15} color="#6366f1" />
          Keterangan Masalah<span style={s.required}>*</span>
        </label>
        <textarea
          name="problem"
          value={form.problem}
          onChange={handleChange}
          placeholder="Deskripsikan kendala / masalah secara detail..."
          style={{ ...inputStyle("problem"), ...s.textarea }}
        />
        {fieldError("problem")}
      </div>

      {/* ── Remote ID & Password (Hanya muncul jika memilih Anydesk/Rustdesk/Ultraviewer) ── */}
      <AnimatePresence>
        {isRemoteTool && (
          <motion.div
            style={s.remoteBox}
            initial={{ opacity: 0, height: 0, y: -10 }}
            animate={{ opacity: 1, height: "auto", y: 0 }}
            exit={{ opacity: 0, height: 0, y: -10 }}
            transition={{ duration: 0.25 }}
          >
            <div style={s.remoteHeader}>
              <Key size={16} />
              Akses Remote ({form.penanganan})
            </div>
            <div style={s.row}>
              <div>
                <label style={{ ...s.label, fontSize: 12 }}>
                  <Hash size={13} color="#4338ca" />
                  ID {form.penanganan}<span style={s.required}>*</span>
                </label>
                <input
                  type="text"
                  name="nomer"
                  value={form.nomer}
                  onChange={handleChange}
                  placeholder={`Masukkan ID ${form.penanganan}`}
                  style={inputStyle("nomer")}
                />
                {fieldError("nomer")}
              </div>

              <div>
                <label style={{ ...s.label, fontSize: 12 }}>
                  <Key size={13} color="#4338ca" />
                  Password {isUltraviewer ? <span style={s.required}>* (Wajib)</span> : <span style={{ color: "#6b7280", fontWeight: 400 }}>(Opsional)</span>}
                </label>
                <input
                  type="text"
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder={isUltraviewer ? "Password UltraViewer (Wajib)" : "Password remote (opsional)"}
                  style={inputStyle("password")}
                />
                {fieldError("password")}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Baris Pelapor + WhatsApp (Otomatis dari data_karyawan: nama_depan & nama_belakang) ── */}
      <div style={s.row}>
        <div style={s.field}>
          <label style={s.label}>
            <User size={15} color="#6366f1" />
            Nama Pelapor<span style={s.required}>*</span>
          </label>
          <input
            type="text"
            name="pelapor"
            value={form.pelapor}
            onChange={handleChange}
            placeholder="Nama Pelapor"
            style={{ ...inputStyle("pelapor"), ...s.inputReadOnly }}
            readOnly
          />
          {fieldError("pelapor")}
        </div>

        <div style={s.field}>
          <label style={s.label}>
            <Phone size={15} color="#6366f1" />
            WhatsApp Pelapor
          </label>
          <input
            type="tel"
            name="wa_pelapor"
            value={form.wa_pelapor}
            onChange={handleChange}
            placeholder="Mis: 0812xxxx"
            style={s.input}
          />
        </div>
      </div>

      {/* ── Buttons ── */}
      <div style={s.btnRow}>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            style={s.btnSecondary}
            disabled={loading}
          >
            Batal
          </button>
        )}
        <button type="submit" style={s.btnPrimary} disabled={loading}>
          {loading ? (
            <>
              <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} />
              Mengirim...
            </>
          ) : (
            "🎫 Kirim Tiket"
          )}
        </button>
      </div>

      <style>{`
        @keyframes spin { from { transform:rotate(0deg) } to { transform:rotate(360deg) } }
      `}</style>
    </form>
  );
};

export default RequestITForm;
